import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  MusicIcon,
  VisualArtIcon,
  WritingIcon,
  OtherIcon,
  Clock,
  Layers,
  ChevronDownIcon,
  ChevronRightIcon,
} from '../icons';
import { Disc, FileText } from 'lucide-react';
import { Card } from '../ui';
import { ProjectCardDto, ProjectDto } from '../../services/projects';
import { SubProjectList } from './SubProjectList';
import './ProjectCard.css';

interface ProjectCardProps {
  project: ProjectCardDto;
  onView?: (project: ProjectCardDto) => void;
  onArchive?: (project: ProjectCardDto) => void;
  onSubProjectClick?: (project: ProjectDto) => void;
  onAddSubProject?: (parentProjectId: string) => void;
  onAddPiece?: (opusId: string) => void;
  isDragging?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  compact?: boolean; // For Piece cards nested in Opus
}

const getProjectIcon = (type: string) => {
  switch (type.toLowerCase()) {
    case 'music':
      return <MusicIcon size={16} />;
    case 'visualart':
    case 'visual_art':
      return <VisualArtIcon size={16} />;
    case 'writing':
      return <WritingIcon size={16} />;
    default:
      return <OtherIcon size={16} />;
  }
};

const getTypeClass = (type: string): string => {
  const normalizedType = type.toLowerCase().replace('_', '');
  return `project-card__type-icon--${normalizedType}`;
};

const getStageClass = (stage: string): string => {
  const normalizedStage = stage.toLowerCase().replace(/\s+/g, '-');
  return `project-card__stage--${normalizedStage}`;
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return date.toLocaleDateString();
};

export const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  onView,
  onArchive,
  onSubProjectClick,
  onAddSubProject,
  onAddPiece,
  isDragging = false,
  dragHandleProps,
  compact = false,
}) => {
  const { t } = useTranslation();
  const [piecesExpanded, setPiecesExpanded] = React.useState(false);
  const isParentProject = project.isParentProject && project.subProjectCount > 0;
  const isOpus = project.category === 'Opus';
  const isPiece = project.category === 'Piece';

  // Handle card click - navigate to details
  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on interactive elements
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('.project-card__pieces-toggle')) {
      return;
    }
    onView?.(project);
  };

  // Build class names
  const cardClasses = [
    'project-card',
    isDragging && 'project-card--dragging',
    isParentProject && 'project-card--parent',
    isOpus && 'project-card--opus',
    isPiece && 'project-card--piece',
    compact && 'project-card--compact',
  ].filter(Boolean).join(' ');

  // Compact Piece card (for nested display)
  if (compact && isPiece) {
    return (
      <div className="project-card project-card--piece project-card--compact" onClick={() => onView?.(project)}>
        <div className="project-card__piece-indicator">
          <FileText size={12} />
        </div>
        <div className="project-card__compact-content">
          <span className="project-card__compact-title">{project.title}</span>
          <span className={`project-card__stage project-card__stage--mini ${getStageClass(project.currentStage)}`}>
            {project.currentStage}
          </span>
        </div>
      </div>
    );
  }

  return (
    <Card className={`${cardClasses} project-card--clickable`} onClick={handleCardClick}>
      <div className="project-card__drag-handle" {...dragHandleProps}>
        <div className="project-card__drag-dots">⋮⋮</div>
      </div>

      <div className="project-card__content">
        {/* Category indicator for Opus */}
        {isOpus && (
          <div className="project-card__opus-header">
            <Disc size={14} />
            <span>{t('works.categories.opus')}</span>
          </div>
        )}

        <div className="project-card__header">
          <div className={`project-card__type-icon ${getTypeClass(project.projectType)}`}>
            {getProjectIcon(project.projectType)}
          </div>
          <h4 className="project-card__title">
            {project.title}
            {isPiece && !compact && (
              <span className="project-card__category-badge project-card__category-badge--piece" title={t('works.categories.piece')}>
                <FileText size={12} />
              </span>
            )}
            {isParentProject && !isOpus && (
              <span className="project-card__parent-badge" title={t('works.subProjects.title')}>
                <Layers size={12} />
              </span>
            )}
          </h4>
        </div>

        {project.description && (
          <p className="project-card__description">{project.description}</p>
        )}

        <div className="project-card__meta">
          <span className={`project-card__stage ${getStageClass(project.currentStage)}`}>{project.currentStage}</span>
          <span className="project-card__sessions">
            {project.sessionCount} {t('common.sessions')}
          </span>
        </div>

        {/* Opus: Show Pieces progress */}
        {isOpus && project.subProjectCount > 0 && (
          <div className="project-card__pieces-progress">
            <button
              className="project-card__pieces-toggle"
              onClick={(e) => { e.stopPropagation(); setPiecesExpanded(!piecesExpanded); }}
            >
              {piecesExpanded ? <ChevronDownIcon size={14} /> : <ChevronRightIcon size={14} />}
              <span className="project-card__pieces-count">
                <strong>{project.completedSubProjectCount}</strong>/{project.subProjectCount} {t('works.piecesCompleted')}
              </span>
            </button>
            <div className="project-card__pieces-bar">
              <div
                className="project-card__pieces-bar-fill"
                style={{ width: `${(project.completedSubProjectCount / project.subProjectCount) * 100}%` }}
              />
            </div>
          </div>
        )}

        <div className="project-card__footer">
          <span className="project-card__last-worked">
            <Clock size={12} />
            {formatDate(project.lastWorkedDate)}
          </span>
        </div>

        {/* Expanded Pieces list for Opus */}
        {isOpus && piecesExpanded && (
          <SubProjectList
            parentProjectId={project.id}
            subProjectCount={project.subProjectCount}
            completedSubProjectCount={project.completedSubProjectCount}
            onSubProjectClick={onSubProjectClick}
            onAddSubProject={() => onAddPiece?.(project.id)}
            isPieceList={true}
          />
        )}

        {/* Legacy Sub-project list for non-Opus parent projects */}
        {isParentProject && !isOpus && (
          <SubProjectList
            parentProjectId={project.id}
            subProjectCount={project.subProjectCount}
            completedSubProjectCount={project.completedSubProjectCount}
            onSubProjectClick={onSubProjectClick}
            onAddSubProject={() => onAddSubProject?.(project.id)}
          />
        )}
      </div>
    </Card>
  );
};

export default ProjectCard;
